import React, { useState, useCallback } from "react";
import {
  Upload,
  ClipboardPaste,
  FileSpreadsheet,
  Download,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Users,
  Loader2,
  Info,
} from "lucide-react";
import { agentLogin, agents as agentsApi } from "../../utils/api";
import LoginEmailResult from "../coach/LoginEmailResult";
import {
  CSV_TEMPLATE,
  REQUIRED_FIELDS,
  VALID_STATUSES,
} from "../../config/agentImport";

const OPTIONAL_FIELDS = ["tags", "notes", "assignedCoach"];
const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

// Common header mappings
const HEADER_MAPPINGS = {
  "first name": "firstName",
  "firstname": "firstName",
  "first": "firstName",
  "last name": "lastName",
  "lastname": "lastName",
  "last": "lastName",
  "email": "email",
  "email address": "email",
  "phone": "phone",
  "phone number": "phone",
  "mobile": "phone",
  "cell": "phone",
  "tags": "tags",
  "tag": "tags",
  "notes": "notes",
  "note": "notes",
  "comments": "notes",
  "start date": "startDate",
  "startdate": "startDate",
  "start": "startDate",
  "date": "startDate",
  "status": "status",
  "substatus": "status",
  "sub status": "status",
  "sub-status": "status",
  "assigned coach": "assignedCoach",
  "assignedcoach": "assignedCoach",
  "coach": "assignedCoach",
};

const AgentImportWizard = ({ onComplete, onCancel, isOnboarding = false }) => {
  const [step, setStep] = useState("method"); // method, upload, paste, mapping, preview, importing, complete
  const [importMethod, setImportMethod] = useState(null);
  const [rawData, setRawData] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [mappedAgents, setMappedAgents] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [sendingLoginEmails, setSendingLoginEmails] = useState(false);
  const [loginEmailResult, setLoginEmailResult] = useState(null);

  // Download CSV template
  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "agent-import-template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Parse CSV content
  const parseCSV = (content) => {
    const lines = content.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    // Parse header row
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine);

    // Parse data rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.some((v) => v.trim())) {
        rows.push(values);
      }
    }

    return { headers, rows };
  };

  // Parse a single CSV line (handles quoted values)
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if ((char === "," || char === "\t") && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());

    return result;
  };

  // Auto-detect column mappings
  const autoDetectMappings = (headers) => {
    const mappings = {};
    headers.forEach((header, index) => {
      const normalized = header.toLowerCase().trim();
      if (HEADER_MAPPINGS[normalized]) {
        mappings[index] = HEADER_MAPPINGS[normalized];
      }
    });
    return mappings;
  };

  // Handle file upload
  const handleFileUpload = useCallback((file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const { headers, rows } = parseCSV(content);
      setHeaders(headers);
      setParsedRows(rows);
      setColumnMapping(autoDetectMappings(headers));
      setStep("mapping");
    };
    reader.readAsText(file);
  }, []);

  // Handle file drop
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file && (file.type === "text/csv" || file.name.endsWith(".csv"))) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload]
  );

  // Handle paste submit
  const handlePasteSubmit = () => {
    if (!rawData.trim()) return;

    const { headers, rows } = parseCSV(rawData);
    setHeaders(headers);
    setParsedRows(rows);
    setColumnMapping(autoDetectMappings(headers));
    setStep("mapping");
  };

  // Apply column mapping and validate
  const applyMappingAndValidate = () => {
    const agents = [];
    const errors = [];

    parsedRows.forEach((row, rowIndex) => {
      const agent = {};
      let hasData = false;

      // Map columns to agent fields
      Object.entries(columnMapping).forEach(([colIndex, fieldName]) => {
        const value = row[parseInt(colIndex)];
        if (value && value.trim()) {
          agent[fieldName] = value.trim();
          hasData = true;
        }
      });

      if (!hasData) return; // Skip empty rows

      // Validate required fields
      const missingFields = REQUIRED_FIELDS.filter(
        (field) => !agent[field] || !agent[field].trim()
      );

      if (missingFields.length > 0) {
        errors.push({
          row: rowIndex + 2, // +2 for header row and 0-index
          agent,
          error: `Missing required fields: ${missingFields.join(", ")}`,
        });
      } else {
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(agent.email)) {
          errors.push({
            row: rowIndex + 2,
            agent,
            error: "Invalid email format",
          });
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(agent.startDate)) {
          errors.push({
            row: rowIndex + 2,
            agent,
            error: "Start date must use YYYY-MM-DD format",
          });
        } else if (!VALID_STATUSES.includes(agent.status.toLowerCase())) {
          errors.push({
            row: rowIndex + 2,
            agent,
            error: `Invalid status. Use one of: ${VALID_STATUSES.join(", ")}`,
          });
        } else {
          agent.status = agent.status.toLowerCase();
          agents.push({ ...agent, _rowIndex: rowIndex + 2 });
        }
      }
    });

    setMappedAgents(agents);
    setValidationErrors(errors);
    setStep("preview");
  };

  // Submit import
  const submitImport = async () => {
    if (mappedAgents.length === 0) return;

    setIsImporting(true);
    setStep("importing");

    try {
      const response = await agentsApi.bulkImport(mappedAgents);
      setImportResults(response.data.results);
      setStep("complete");
    } catch (error) {
      console.error("Import failed:", error);
      setImportResults(
        error.response?.data?.results || {
          successful: 0,
          failed: mappedAgents.length,
          skipped: 0,
          created: [],
          skippedDuplicates: [],
          errors: [{ error: error.response?.data?.message || "Import failed" }],
        }
      );
      setStep("complete");
    } finally {
      setIsImporting(false);
    }
  };

  const sendImportedAgentLoginEmails = async (agentIds = null) => {
    const createdAgents = importResults?.created || [];
    const ids = (agentIds || createdAgents.map((agent) => agent.id)).filter(Boolean);
    if (!ids.length) return;

    setSendingLoginEmails(true);
    setLoginEmailResult(null);
    try {
      const response = await agentLogin.send({ agentIds: ids });
      setLoginEmailResult(response.data);
    } catch (_error) {
      setLoginEmailResult({
        total: ids.length,
        sent: 0,
        failed: ids.length,
        results: ids.map((agentId) => ({ agentId, status: "failed" })),
      });
    } finally {
      setSendingLoginEmails(false);
    }
  };

  // Render method selection
  const renderMethodSelection = () => (
    <div id="import-method-selection" className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isOnboarding ? "Add Your Agents" : "Import Agents"}
        </h2>
        <p className="text-gray-600">
          {isOnboarding
            ? "Let's get your team loaded in. This takes about 2 minutes."
            : "Choose how you'd like to add agents to your organization."}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <button
          onClick={() => {
            setImportMethod("upload");
            setStep("upload");
          }}
          className="group p-6 border-2 border-gray-200 rounded-xl hover:border-brand-primary hover:bg-brand-primary/5 transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
              <Upload className="w-6 h-6 text-brand-primary" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Upload Spreadsheet
          </h3>
          <p className="text-sm text-gray-500">
            Upload a CSV file from your computer
          </p>
        </button>

        <button
          onClick={() => {
            setImportMethod("paste");
            setStep("paste");
          }}
          className="group p-6 border-2 border-gray-200 rounded-xl hover:border-brand-primary hover:bg-brand-primary/5 transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
              <ClipboardPaste className="w-6 h-6 text-brand-primary" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Copy & Paste
          </h3>
          <p className="text-sm text-gray-500">
            Paste data directly from Excel or Google Sheets
          </p>
        </button>
      </div>

      <div className="flex items-center justify-center pt-4">
        <button
          onClick={downloadTemplate}
          className="inline-flex items-center text-sm text-brand-primary hover:text-brand-primary/80 transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          Download CSV template
        </button>
      </div>

      {!isOnboarding && onCancel && (
        <div className="flex justify-center pt-4">
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  // Render file upload
  const renderFileUpload = () => (
    <div id="import-file-upload" className="space-y-6">
      <div className="flex items-center mb-4">
        <button
          onClick={() => setStep("method")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors mr-2"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl font-bold text-gray-900">Upload Spreadsheet</h2>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
          dragActive
            ? "border-brand-primary bg-brand-primary/5"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 mb-2">
          Drag and drop your CSV file here, or
        </p>
        <label className="btn-brand-primary cursor-pointer">
          <Upload className="w-4 h-4 mr-2" />
          Choose File
          <input
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files[0])}
          />
        </label>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Required columns:</p>
            <p>firstName, lastName, email, phone, startDate, status</p>
            <p className="mt-2 text-blue-600">
              startDate must be YYYY-MM-DD. Optional: tags, notes, assignedCoach
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render paste interface
  const renderPasteInterface = () => (
    <div id="import-paste-interface" className="space-y-6">
      <div className="flex items-center mb-4">
        <button
          onClick={() => setStep("method")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors mr-2"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl font-bold text-gray-900">Copy & Paste Data</h2>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">How to paste from a spreadsheet:</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Select your data in Excel or Google Sheets (including headers)</li>
              <li>Copy (Ctrl+C or Cmd+C)</li>
              <li>Paste into the box below (Ctrl+V or Cmd+V)</li>
            </ol>
          </div>
        </div>
      </div>

      <textarea
        value={rawData}
        onChange={(e) => setRawData(e.target.value)}
        placeholder="Paste your spreadsheet data here...

Example:
firstName	lastName	email	phone	startDate	status
John	Doe	john@example.com	555-1234	2026-07-01	active
Jane	Smith	jane@example.com	555-5678	2026-07-01	pre-license"
        className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
      />

      <div className="flex justify-end">
        <button
          onClick={handlePasteSubmit}
          disabled={!rawData.trim()}
          className="btn-brand-primary"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </div>
  );

  // Render column mapping
  const renderColumnMapping = () => (
    <div id="import-column-mapping" className="space-y-6">
      <div className="flex items-center mb-4">
        <button
          onClick={() => setStep(importMethod === "upload" ? "upload" : "paste")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors mr-2"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl font-bold text-gray-900">Map Your Columns</h2>
      </div>

      <p className="text-gray-600">
        We detected {headers.length} columns and {parsedRows.length} rows. Please
        confirm the column mappings below.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Your Column
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Maps To
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Sample Data
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {headers.map((header, index) => (
              <tr key={index}>
                <td className="px-4 py-3 text-sm text-gray-900">{header}</td>
                <td className="px-4 py-3">
                  <select
                    value={columnMapping[index] || ""}
                    onChange={(e) =>
                      setColumnMapping({
                        ...columnMapping,
                        [index]: e.target.value || undefined,
                      })
                    }
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  >
                    <option value="">-- Skip this column --</option>
                    {ALL_FIELDS.map((field) => (
                      <option key={field} value={field}>
                        {field}
                        {REQUIRED_FIELDS.includes(field) ? " *" : ""}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {parsedRows[0]?.[index] || "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center text-sm text-gray-500">
        <span className="text-red-500 mr-1">*</span> Required fields
      </div>

      <div className="flex justify-end">
        <button
          onClick={applyMappingAndValidate}
          className="btn-brand-primary"
        >
          Preview Import
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </div>
  );

  // Render preview
  const renderPreview = () => (
    <div id="import-preview" className="space-y-6">
      <div className="flex items-center mb-4">
        <button
          onClick={() => setStep("mapping")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors mr-2"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl font-bold text-gray-900">Review & Import</h2>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm text-green-700">Ready to import</span>
          </div>
          <p className="text-2xl font-bold text-green-800 mt-1">
            {mappedAgents.length}
          </p>
        </div>

        {validationErrors.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
              <span className="text-sm text-yellow-700">Issues found</span>
            </div>
            <p className="text-2xl font-bold text-yellow-800 mt-1">
              {validationErrors.length}
            </p>
          </div>
        )}
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-medium text-yellow-800 mb-2">
            The following rows have issues and will be skipped:
          </h3>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {validationErrors.map((err, index) => (
              <div key={index} className="text-sm text-yellow-700">
                <span className="font-medium">Row {err.row}:</span> {err.error}
                {err.agent.email && (
                  <span className="text-yellow-600 ml-1">
                    ({err.agent.firstName} {err.agent.lastName})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview table */}
      {mappedAgents.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mappedAgents.slice(0, 10).map((agent, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {agent.firstName} {agent.lastName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {agent.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {agent.phone || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {agent.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {mappedAgents.length > 10 && (
            <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 text-center">
              And {mappedAgents.length - 10} more agents...
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          onClick={() => setStep("mapping")}
          className="btn-brand-secondary-outline"
        >
          Back
        </button>
        <button
          onClick={submitImport}
          disabled={mappedAgents.length === 0}
          className="btn-brand-primary"
        >
          <Users className="w-4 h-4 mr-2" />
          Import {mappedAgents.length} Agents
        </button>
      </div>
    </div>
  );

  // Render importing state
  const renderImporting = () => (
    <div id="import-in-progress" className="text-center py-12">
      <Loader2 className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
      <h2 className="text-xl font-bold text-gray-900 mb-2">Importing Agents...</h2>
      <p className="text-gray-600">This may take a moment</p>
    </div>
  );

  // Render complete
  const renderComplete = () => (
    <div id="import-complete" className="text-center py-12">
      {importResults?.successful > 0 ? (
        <>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Complete!</h2>
          <p className="text-gray-600 mb-6">
            Created {importResults.successful} agent
            {importResults.successful !== 1 ? "s" : ""}
            {importResults.skipped > 0 && (
              <span className="text-yellow-600">
                {" "}
                ({importResults.skipped} skipped as duplicates)
              </span>
            )}
          </p>
          <p className="text-sm text-gray-600 mb-6">
            No password or account setup is needed. Each new agent can sign in
            with the email you imported.
          </p>
        </>
      ) : (
        <>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Failed</h2>
          <p className="text-gray-600 mb-6">
            No agents were imported. Please check your data and try again.
          </p>
        </>
      )}

      {/* Show errors if any */}
      {importResults?.errors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mb-6 max-w-lg mx-auto">
          <h3 className="font-medium text-red-800 mb-2">Errors:</h3>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {importResults.errors.slice(0, 5).map((err, index) => (
              <div key={index} className="text-sm text-red-700">
                {err.row && <span className="font-medium">Row {err.row}: </span>}
                {err.error}
              </div>
            ))}
            {importResults.errors.length > 5 && (
              <div className="text-sm text-red-600">
                And {importResults.errors.length - 5} more errors...
              </div>
            )}
          </div>
        </div>
      )}

      {importResults?.created?.length > 0 && (
        <div
          id="import-created-report"
          className="bg-green-50 border border-green-200 rounded-lg p-4 text-left mb-6 max-w-lg mx-auto"
        >
          <h3 className="font-medium text-green-800 mb-2">Created:</h3>
          <div id="import-created-report-list" className="max-h-32 overflow-y-auto space-y-1">
            {importResults.created.slice(0, 5).map((created) => (
              <div
                id={`import-created-${created.id}`}
                key={created.id}
                className="text-sm text-green-700"
              >
                {created.name} ({created.email})
              </div>
            ))}
            {importResults.created.length > 5 && (
              <div id="import-created-more" className="text-sm text-green-600">
                And {importResults.created.length - 5} more...
              </div>
            )}
          </div>
        </div>
      )}

      <div id="import-login-email-result" className="max-w-lg mx-auto mb-6 text-left">
        <LoginEmailResult
          result={loginEmailResult}
          agentNames={(importResults?.created || []).reduce(
            (names, created) => ({ ...names, [created.id]: created.name }),
            {}
          )}
          onRetryFailed={sendImportedAgentLoginEmails}
          retrying={sendingLoginEmails}
          idPrefix="import-login-email"
        />
      </div>

      {/* Show skipped duplicates if any */}
      {importResults?.skippedDuplicates?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left mb-6 max-w-lg mx-auto">
          <h3 className="font-medium text-yellow-800 mb-2">
            Skipped Duplicates:
          </h3>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {importResults.skippedDuplicates.slice(0, 5).map((dup, index) => (
              <div key={index} className="text-sm text-yellow-700">
                {dup.name} ({dup.email})
              </div>
            ))}
            {importResults.skippedDuplicates.length > 5 && (
              <div className="text-sm text-yellow-600">
                And {importResults.skippedDuplicates.length - 5} more...
              </div>
            )}
          </div>
        </div>
      )}

      <div id="import-complete-actions" className="flex flex-col sm:flex-row-reverse justify-center gap-3">
        {importResults?.created?.length > 0 && (
          <button
            id="send-imported-agent-login-emails"
            type="button"
            onClick={() => sendImportedAgentLoginEmails()}
            disabled={sendingLoginEmails}
            className="btn-brand-primary min-h-11"
          >
            {sendingLoginEmails ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                Sending login emails…
              </>
            ) : (
              `Send login emails to ${importResults.created.length} new agent${
                importResults.created.length === 1 ? "" : "s"
              }`
            )}
          </button>
        )}
        <button
          id="view-imported-agents"
          type="button"
          onClick={onComplete}
          className="btn-brand-secondary-outline min-h-11"
        >
          View agents
          <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  return (
    <div id="agent-import-wizard" className="max-w-3xl mx-auto">
      {step === "method" && renderMethodSelection()}
      {step === "upload" && renderFileUpload()}
      {step === "paste" && renderPasteInterface()}
      {step === "mapping" && renderColumnMapping()}
      {step === "preview" && renderPreview()}
      {step === "importing" && renderImporting()}
      {step === "complete" && renderComplete()}
    </div>
  );
};

export default AgentImportWizard;
